import type { TFunction } from "i18next";
import { locateRow } from "../find";
import { isBlockDrag } from "./rules";
import type { DragData, DropData } from "./types";
import type { DocumentSection } from "../types";

type RowSlotContext =
  | { scope: 'document' }
  | { scope: 'group'; groupId: string };

function resolveTargetColumnNumber(
  sections: DocumentSection[],
  rowId: string,
  columnId: string,
): number | null {
  const located = locateRow(sections, rowId);
  if (!located) {
    return null;
  }

  const index = located.row.columns.findIndex(column => column.id === columnId);
  return index >= 0 ? index + 1 : null;
}

function resolveBlockSlotLabel(
  drag: DragData,
  drop: Extract<DropData, { kind: 'block-slot' }>,
  sections: DocumentSection[],
  t: TFunction,
): string {
  const located = locateRow(sections, drop.rowId);
  const multiColumn = (located?.row.columns.length ?? 0) > 1;
  const columnNumber = resolveTargetColumnNumber(
    sections,
    drop.rowId,
    drop.columnId,
  );

  if (drag.kind === 'item-block') {
    if (drag.rowId === drop.rowId && drag.columnId === drop.columnId) {
      return t("Move here");
    }

    if (multiColumn && columnNumber !== null) {
      return t("Move to Column {{number}}", { number: columnNumber });
    }

    return t("Move here");
  }

  if (drag.kind === 'palette-block') {
    if (multiColumn && columnNumber !== null) {
      return t("Add to Column {{number}}", { number: columnNumber });
    }

    return t("Add to row");
  }

  return t("Drop here");
}

export function resolveDropIndicatorLabel(
  drag: DragData | undefined,
  drop: DropData,
  sections: DocumentSection[],
  t: TFunction,
): string {
  if (!drag) {
    return t("Drop here");
  }

  if (drop.kind === 'block-slot') {
    return resolveBlockSlotLabel(drag, drop, sections, t);
  }

  if (drop.kind === 'section-slot') {
    return resolveRowSlotLabel(drag, sections, { scope: 'document' }, t);
  }

  if (drop.kind === 'group-row-slot') {
    return resolveRowSlotLabel(drag, sections, {
      scope: 'group',
      groupId: drop.groupId,
    }, t);
  }

  return t("Drop here");
}

function resolveRowSlotLabel(
  drag: DragData,
  sections: DocumentSection[],
  context: RowSlotContext,
  t: TFunction,
): string {
  if (isBlockDrag(drag) || drag.kind === 'palette-row') {
    return context.scope === 'group' ? t("New row in group") : t("New row");
  }

  if (drag.kind === 'palette-section') {
    return t("Insert section");
  }

  if (drag.kind === 'palette-group') {
    return t("New group");
  }

  if (drag.kind === 'item-group') {
    return t("Move group");
  }

  if (drag.kind === 'item-row') {
    const located = locateRow(sections, drag.rowId);
    if (!located) {
      return t("Drop here");
    }

    if (context.scope === 'group') {
      if (
        located.location.parent === 'group' &&
        located.location.groupId === context.groupId
      ) {
        return t("Move row");
      }
      return t("Move to group");
    }

    return t("Move row");
  }

  return t("Drop here");
}
